import {
    SEVERITY_KEY_BY_SCORE,
    SEVERITY_LABEL_EN_BY_KEY,
    SEVERITY_SCORE_BY_KEY,
} from './constants.js';
import { deriveBiaImpactStatus } from './impact-status.js';
import { formatHorizonDisplayLabel } from './time-horizon.js';

const MODEL_DESCRIPTIONS = {
    SIMPLE_AVERAGE: 'Each time horizon uses the equal-weight average of category severity scores.',
    WEIGHTED_AVERAGE: 'Each time horizon uses the weighted average of category severity scores.',
    SINGLE_THRESHOLD: 'Each time horizon is reached when any single category meets or exceeds the threshold.',
};

/**
 * @param {import('./resolve.js').EffectiveBiaImpactConfiguration} config
 * @param {Array<{ categoryKey: string, scores?: Record<string, string>, values?: Record<string, string> }>} scoreRows
 * @returns {number}
 */
function getCategoryWeight(config, categoryKey) {
    const cat = config.categories.find((c) => c.categoryKey === categoryKey);
    const w = Number(cat?.weight);
    return Number.isFinite(w) && w > 0 ? w : 1;
}

/**
 * @param {Record<string, string>|Map<string, string>|undefined} values
 * @param {string} horizonId
 * @returns {string}
 */
function getCellSeverityKey(values, horizonId) {
    const map = values instanceof Map ? Object.fromEntries(values.entries()) : (values || {});
    return map[horizonId] || 'NO_IMPACT';
}

/**
 * @param {Record<string, string>|Map<string, string>|undefined} values
 * @param {string} horizonId
 * @returns {boolean}
 */
function isCategoryParticipating(values, horizonId) {
    return getCellSeverityKey(values, horizonId) !== 'NO_IMPACT';
}

/**
 * @param {Record<string, string>|Map<string, string>|undefined} values
 * @param {string} horizonId
 * @returns {number}
 */
function scoreForCell(values, horizonId) {
    const key = getCellSeverityKey(values, horizonId);
    return SEVERITY_SCORE_BY_KEY[key] ?? 0;
}

/**
 * @param {import('./resolve.js').EffectiveBiaImpactConfiguration} config
 * @param {Array<{ categoryKey: string, scores?: Record<string, string>, values?: Record<string, string> }>} scoreRows
 * @param {string} horizonId
 * @returns {number}
 */
function computeHorizonAggregateScore(config, scoreRows, horizonId) {
    const model = config.calculationModel;
    const rows = scoreRows || [];

    if (rows.length === 0) return 0;

    if (model === 'SINGLE_THRESHOLD') {
        let max = 0;
        for (const row of rows) {
            const values = row.scores || row.values;
            if (!isCategoryParticipating(values, horizonId)) continue;
            const score = scoreForCell(values, horizonId);
            if (score > max) max = score;
        }
        return max;
    }

    if (model === 'WEIGHTED_AVERAGE') {
        let weightedSum = 0;
        let weightTotal = 0;
        for (const row of rows) {
            const values = row.scores || row.values;
            if (!isCategoryParticipating(values, horizonId)) continue;
            const score = scoreForCell(values, horizonId);
            const weight = getCategoryWeight(config, row.categoryKey);
            weightedSum += score * weight;
            weightTotal += weight;
        }
        if (weightTotal <= 0) return 0;
        return weightedSum / weightTotal;
    }

    let sum = 0;
    let count = 0;
    for (const row of rows) {
        const values = row.scores || row.values;
        if (!isCategoryParticipating(values, horizonId)) continue;
        sum += scoreForCell(values, horizonId);
        count += 1;
    }
    if (count <= 0) return 0;
    return sum / count;
}

/**
 * @param {import('./resolve.js').EffectiveBiaImpactConfiguration} config
 * @param {Array<{ categoryKey: string, scores?: Record<string, string>, values?: Record<string, string> }>} scoreRows
 * @param {string} horizonId
 * @param {number} threshold
 * @returns {boolean}
 */
function isHorizonThresholdMet(config, scoreRows, horizonId, threshold) {
    if (config.calculationModel === 'SINGLE_THRESHOLD') {
        for (const row of scoreRows || []) {
            const values = row.scores || row.values;
            if (!isCategoryParticipating(values, horizonId)) continue;
            const score = scoreForCell(values, horizonId);
            if (score >= threshold) return true;
        }
        return false;
    }
    const aggregate = computeHorizonAggregateScore(config, scoreRows, horizonId);
    return aggregate >= threshold;
}

/**
 * @param {import('./resolve.js').EffectiveBiaImpactConfiguration} config
 * @param {Array<{ categoryKey: string, scores?: Record<string, string>, values?: Record<string, string> }>} scoreRows
 * @param {number} threshold
 * @returns {{ horizonId: string, displayLabel: string, durationMinutes: number } | null}
 */
function findFirstThresholdHorizon(config, scoreRows, threshold) {
    for (const horizon of config.timeHorizons) {
        if (isHorizonThresholdMet(config, scoreRows, horizon.id, threshold)) {
            return {
                horizonId: horizon.id,
                displayLabel: horizon.displayLabel || formatHorizonDisplayLabel(horizon),
                durationMinutes: horizon.durationMinutes,
            };
        }
    }
    return null;
}

/**
 * @param {number} aggregateScore
 * @returns {string}
 */
function aggregateToSeverityKey(aggregateScore) {
    const rounded = Math.min(5, Math.max(0, Math.round(aggregateScore)));
    return SEVERITY_KEY_BY_SCORE[rounded] || 'NO_IMPACT';
}

/**
 * @param {import('./resolve.js').EffectiveBiaImpactConfiguration} config
 * @param {Array<{ categoryKey: string, scores?: Record<string, string>, values?: Record<string, string> }>} scoreRows
 * @returns {import('./compute.js').BiaRecoveryResult}
 */
export function computeBiaRecovery(config, scoreRows) {
    const warnings = [];
    const horizonScores = (config.timeHorizons || []).map((horizon) => {
        const score = computeHorizonAggregateScore(config, scoreRows, horizon.id);
        const severityKey = aggregateToSeverityKey(score);
        const thresholdMet = config.calculationModel === 'SINGLE_THRESHOLD'
            ? isHorizonThresholdMet(config, scoreRows, horizon.id, config.mtpdThreshold)
            : undefined;
        return {
            horizonId: horizon.id,
            score: Math.round(score * 100) / 100,
            severityKey,
            label: SEVERITY_LABEL_EN_BY_KEY[severityKey] || severityKey,
            thresholdMet,
        };
    });

    const mtpd = findFirstThresholdHorizon(config, scoreRows, config.mtpdThreshold);
    if (!mtpd) {
        warnings.push('mtpd_threshold_never_reached');
    }

    let rto = findFirstThresholdHorizon(config, scoreRows, config.rtoThreshold);
    if (!rto) {
        warnings.push('rto_threshold_never_reached');
    }

    let rtoCappedByMtpd = false;
    if (rto && mtpd && rto.durationMinutes > mtpd.durationMinutes) {
        rto = { ...mtpd };
        rtoCappedByMtpd = true;
        warnings.push('rto_capped_by_mtpd');
    }

    let rpo = findFirstThresholdHorizon(config, scoreRows, config.rpoThreshold);
    if (!rpo) {
        warnings.push('rpo_threshold_never_reached');
    }

    let rpoCappedByRto = false;
    if (rpo && rto && rpo.durationMinutes > rto.durationMinutes) {
        rpo = { ...rto };
        rpoCappedByRto = true;
        warnings.push('rpo_capped_by_rto');
    }

    return {
        calculationModel: config.calculationModel,
        modelDescription: MODEL_DESCRIPTIONS[config.calculationModel] || '',
        horizonScores,
        mtpd,
        rto,
        rpo,
        rtoCappedByMtpd,
        rpoCappedByRto,
        warnings,
    };
}

/**
 * @typedef {Object} BiaRecoveryResult
 * @property {string} calculationModel
 * @property {string} modelDescription
 * @property {Array<{ horizonId: string, score: number|null, severityKey: string, label: string }>} horizonScores
 * @property {{ horizonId: string, displayLabel: string, durationMinutes: number }|null} mtpd
 * @property {{ horizonId: string, displayLabel: string, durationMinutes: number }|null} rto
 * @property {{ horizonId: string, displayLabel: string, durationMinutes: number }|null} rpo
 * @property {boolean} rtoCappedByMtpd
 * @property {boolean} rpoCappedByRto
 * @property {string[]} warnings
 */

/**
 * @param {import('./resolve.js').EffectiveBiaImpactConfiguration} config
 * @param {Array<{ categoryKey: string, scores?: Record<string, string>, values?: Record<string, string> }>} scoreRows
 * @returns {object}
 */
export function buildImpactAssessmentPayload(config, scoreRows) {
    const recovery = computeBiaRecovery(config, scoreRows);
    const { average: impactStatusAverage, status: impactStatus } = deriveBiaImpactStatus(recovery.horizonScores);
    const now = new Date();

    return {
        scores: (scoreRows || []).map((row) => ({
            categoryKey: row.categoryKey,
            values: row.scores || row.values || {},
        })),
        horizonSummaries: recovery.horizonScores.map((hs) => ({
            horizonId: hs.horizonId,
            aggregateScore: hs.score,
            severityKey: hs.severityKey,
            label: hs.label,
        })),
        recovery: {
            calculationModel: recovery.calculationModel,
            mtpdHorizonId: recovery.mtpd?.horizonId ?? null,
            mtpdDisplay: recovery.mtpd?.displayLabel ?? null,
            rtoHorizonId: recovery.rto?.horizonId ?? null,
            rtoDisplay: recovery.rto?.displayLabel ?? null,
            rpoHorizonId: recovery.rpo?.horizonId ?? null,
            rpoDisplay: recovery.rpo?.displayLabel ?? null,
            rtoCappedByMtpd: recovery.rtoCappedByMtpd,
            rpoCappedByRto: recovery.rpoCappedByRto,
            impactStatus,
            impactStatusAverage,
            warnings: recovery.warnings,
            computedAt: now,
        },
        rto: recovery.rto?.displayLabel ?? null,
        mtpd: recovery.mtpd?.displayLabel ?? null,
        rpo: recovery.rpo?.displayLabel ?? null,
        status: impactStatus,
        impactStatusAverage,
        recoveryResult: recovery,
    };
}

/**
 * @param {import('./resolve.js').EffectiveBiaImpactConfiguration} config
 * @param {Array<{ categoryKey: string, scores?: Record<string, string>, values?: Record<string, string> }>} scoreRows
 * @returns {string[]}
 */
export function validateImpactScores(config, scoreRows) {
    const errors = [];
    const horizonIds = new Set((config.timeHorizons || []).map((h) => h.id));
    const categoryKeys = new Set((config.categories || []).map((c) => c.categoryKey));

    for (const row of scoreRows || []) {
        if (!categoryKeys.has(row.categoryKey)) {
            errors.push(`Unknown category: ${row.categoryKey}`);
        }
        const values = row.scores || row.values || {};
        for (const [horizonId, severityKey] of Object.entries(values)) {
            if (!horizonIds.has(horizonId)) {
                errors.push(`Unknown horizon: ${horizonId}`);
            }
            if (SEVERITY_SCORE_BY_KEY[severityKey] === undefined) {
                errors.push(`Unknown severity: ${severityKey}`);
            }
        }
    }
    return errors;
}

/**
 * @param {import('./resolve.js').EffectiveBiaImpactConfiguration} config
 * @param {Array<{ categoryKey: string, values?: Record<string, string> }>|null|undefined} existingScores
 * @returns {Array<{ categoryKey: string, scores: Record<string, string> }>}
 */
export function buildDefaultScoreGrid(config, existingScores) {
    const byCategory = new Map();
    for (const row of existingScores || []) {
        const values = row.values instanceof Map
            ? Object.fromEntries(row.values.entries())
            : (row.values || row.scores || {});
        byCategory.set(row.categoryKey, { ...values });
    }

    return (config.categories || []).map((cat) => {
        const existing = byCategory.get(cat.categoryKey) || {};
        const scores = {};
        for (const horizon of config.timeHorizons || []) {
            scores[horizon.id] = existing[horizon.id] || 'NO_IMPACT';
        }
        return { categoryKey: cat.categoryKey, scores };
    });
}
